/* eslint-disable @typescript-eslint/no-explicit-any */
import { zodResolver } from '@hookform/resolvers/zod';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm, type Resolver } from 'react-hook-form';
import { z } from 'zod';
import { useNavigationBlocker } from '../../hooks/useNavigationBlocker';
import { useProjectScope } from '../../hooks/useProjectScope';

import DiscardChangesDialog from '../../components/dialogs/discard-changes-dialog';
import FormInput from '../../components/forms/form-input';
import FormSelect from '../../components/forms/form-select';
import FormSearchableSelect from '../../components/forms/form-searchable-select';
import { unitTypesService } from '../../services/unit-types.service';
import { masterService } from '../../services/master.service';
import { ministriesService } from '../../services/ministries.service';
import { projectsService } from '../../services/projects.service';
import type { Department } from '../../types';
import { PageSkeleton } from '../../components/common/panel';
import ScreenHeader from '../../components/common/screen-header';

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

const extractApiError = (error: any): string | null => {
  // Handle new API response format: { detail: { success, code, message, data, error } }
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object') {
    return detail.message || detail.error?.errorCode || null;
  }
  // Handle direct message formats
  const msg =
    error?.response?.data?.message ??
    error?.response?.data?.error ??
    error?.message;
  return msg ? String(msg) : null;
};

// -----------------------------------------------------------------------------
// Zod Schema
// -----------------------------------------------------------------------------

const unitTypeSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .max(100, 'Must be ≤ 100 characters')
    .regex(/^[a-zA-Z\s\-_]+$/, 'Name may contain only alphabets, spaces, hyphens and underscores')
    .refine((val) => (val.match(/[a-zA-Z]/g) || []).length >= 2, 'Name must contain at least 2 alphabet characters'),
  departmentId: z.string().trim().optional().or(z.literal('')),
  appId: z.string().trim().optional().or(z.literal('')),
  level: z.coerce.number({ message: 'Level is required' }).int('Level must be an integer').min(0, 'Level must be 0 or greater'),
});

export type UnitTypeFormData = z.infer<typeof unitTypeSchema>;

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const UnitTypeForm = () => {
  const { id, isReady, navigateToList } = useSecureNavigation({
    entity: 'unit-types',
    basePath: '/unit-types',
  });
  const isEditMode = Boolean(id);
  const queryClient = useQueryClient();
  const toast = useRef<Toast>(null);

  // Ministry is a FILTER only — the backend derives a unit type's ministry from its
  // department, so we don't submit it. It just narrows the department list here.
  const [ministryFilter, setMinistryFilter] = useState('');

  // Fetch ministries for the cascade filter
  const { data: ministries = [] } = useQuery({
    queryKey: ['ministries'],
    queryFn: ministriesService.getAllForDropdown,
  });

  const ministryOptions = ministries.map((m) => ({ value: m._id, label: m.name }));

  // Fetch departments for dropdown
  const { data: departments = [] } = useQuery({
    queryKey: ['departments'],
    queryFn: masterService.getDepartments,
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<UnitTypeFormData>({
    resolver: zodResolver(unitTypeSchema) as Resolver<UnitTypeFormData>,
    defaultValues: {
      name: '',
      departmentId: '',
      appId: '',
      level: undefined,
    },
    mode: 'onChange',
  });

  // Projects the caller may create in (platform admin = all, else own).
  const { data: accessibleProjects = [] } = useQuery({
    queryKey: ['projects', 'accessible'],
    queryFn: projectsService.getAccessible,
    staleTime: 5 * 60 * 1000,
  });
  const projectOptions = accessibleProjects.map((p) => ({
    value: p._id,
    label: p.appCode ? `${p.name} (${p.appCode})` : p.name,
  }));
  const projectLocked = accessibleProjects.length <= 1;
  const selectedAppId = watch('appId');
  useEffect(() => {
    if (!isEditMode && !selectedAppId && accessibleProjects.length) {
      setValue('appId', accessibleProjects[0]._id);
    }
  }, [isEditMode, selectedAppId, accessibleProjects, setValue]);

  // A unit type's department must be in the same project or global (backend rule).
  // Departments cascade from the ministry filter, then scope to the selected project
  // (+ global), each tagged with its owning project. Ministry is only a UI filter, so
  // it is not project-scoped.
  const { belongsToProject, projectTag } = useProjectScope(selectedAppId, accessibleProjects);

  const departmentOptions = departments
    .filter((d: Department) => !ministryFilter || d.ministryId === ministryFilter)
    .filter((d: Department) => belongsToProject(d.appId))
    .map((d: Department) => ({ value: d._id, label: d.name, ...projectTag(d.appId) }));

  // Search handler for departments (respects the ministry filter + project scope)
  const handleDepartmentSearch = async (searchTerm: string) => {
    try {
      const allDepartments = await masterService.getDepartments();
      const filtered = allDepartments.filter((d: Department) =>
        (!ministryFilter || d.ministryId === ministryFilter) &&
        belongsToProject(d.appId) &&
        d.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      return filtered.map((d: Department) => ({ value: d._id, label: d.name, ...projectTag(d.appId) }));
    } catch (error) {
      console.error('Error searching departments:', error);
      return [];
    }
  };

  // Clear the selected department if it no longer belongs to the chosen project
  // (skipped on initial edit-load, which is already consistent).
  const selectedDepartmentId = watch('departmentId');
  const prevAppIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const prev = prevAppIdRef.current;
    prevAppIdRef.current = selectedAppId;
    if (prev === undefined || prev === selectedAppId || !selectedAppId) return;
    const d = departments.find((x: Department) => x._id === selectedDepartmentId);
    if (d?.appId && d.appId !== selectedAppId) setValue('departmentId', '');
  }, [selectedAppId, departments, selectedDepartmentId, setValue]);

  const { showLeaveDialog, confirmLeave, cancelLeave, handleNavigate } = useNavigationBlocker({ when: isDirty });

  // Fetch existing entity when editing
  const { data: existing, isLoading: fetchLoading } = useQuery({
    queryKey: ['unit-types', id],
    queryFn: async () => await unitTypesService.getById(id!),
    enabled: isEditMode && isReady && !!id,
  });

  // Set breadcrumb title to show unit type name instead of UUID in edit mode
  useBreadcrumbTitle(isEditMode ? existing?.name : null);

  useEffect(() => {
    if (existing && isEditMode) {
      reset({
        name: existing.name,
        departmentId: existing.departmentId || '',
        appId: (existing as any).appId || '',
        level: existing.level,
      });
      // Seed the ministry filter from the record's ministry so the department stays visible.
      if (existing.ministryId) setMinistryFilter(existing.ministryId);
    }
  }, [existing, isEditMode, reset]);

  // Mutations
  const createMut = useMutation({
    mutationFn: (payload: any) => unitTypesService.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unit-types'] });
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Unit Type created successfully', life: 3000 });
      setTimeout(() => navigateToList(), 800);
    },
    onError: (error: any) => {
      const apiMsg = extractApiError(error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: apiMsg ? `Save failed: ${apiMsg}` : 'Failed to save Unit Type',
        life: 10000,
      });
    },
  });

  const updateMut = useMutation({
    mutationFn: (payload: any) => unitTypesService.update(id!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unit-types'] });
      queryClient.invalidateQueries({ queryKey: ['unit-types', id] });
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Unit Type updated successfully', life: 3000 });
      setTimeout(() => navigateToList(), 800);
    },
    onError: (error: any) => {
      const apiMsg = extractApiError(error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: apiMsg ? `Save failed: ${apiMsg}` : 'Failed to save Unit Type',
        life: 10000,
      });
    },
  });

  // Combined loading state for button disable
  const isSaving = createMut.isPending || updateMut.isPending;

  // Submit
  const onSubmit = (data: UnitTypeFormData) => {
    const payload: { name: string; departmentId?: string; appId?: string; level: number } = {
      name: data.name,
      departmentId: data.departmentId || undefined,
      level: data.level,
    };

    // Owning project (honoured by the BE only for callers who can access it).
    if (data.appId) payload.appId = data.appId;

    if (isEditMode) updateMut.mutate(payload);
    else createMut.mutate(payload);
  };

  // Loading state
  if (fetchLoading) {
    return <PageSkeleton panels={3} />;
  }

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <div className="py-4 px-4 lg:px-6 bg-canvas dark:bg-gray-900" data-testid="SCR-UnitType-Form">
        {/* Header - Compact */}
        <ScreenHeader
          icon="pi pi-tags"
          title={isEditMode ? 'Edit Unit Type' : 'Create Unit Type'}
          subtitle={isEditMode ? 'Update unit type information' : 'Add a new unit type'}
          onBack={() => handleNavigate('/unit-types')}
        />

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="ds-card p-4 mb-3">
            <h2 className="text-base font-semibold text-ink mb-3">
              Unit Type Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Owning project */}
              <div className="md:col-span-2">
                <Controller
                  name="appId"
                  control={control}
                  render={({ field: { value, onChange, ...field } }) => (
                    <FormSelect
                      {...field}
                      value={value || ''}
                      onChange={(e) => onChange(e.target.value)}
                      label="Project"
                      options={projectOptions}
                      placeholder="Select project"
                      disabled={projectLocked || isEditMode}
                      error={!!errors.appId}
                      helperText={errors.appId?.message}
                    />
                  )}
                />
              </div>

              {/* Name */}
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <FormInput
                    {...field}
                    label="Name"
                    required
                    placeholder="Enter unit type name"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                )}
              />

              {/* Level */}
              <Controller
                name="level"
                control={control}
                render={({ field }) => (
                  <FormInput
                    {...field}
                    type="number"
                    value={field.value ?? ''}
                    label="Level"
                    placeholder="Enter level (number)"
                    error={!!errors.level}
                    helperText={errors.level?.message}
                    required
                  />
                )}
              />

              {/* Ministry - filters the Department list (not submitted; BE derives it) */}
              <div className="md:col-span-2">
                <FormSearchableSelect
                  name="ministryFilter"
                  value={ministryFilter}
                  onChange={(e) => {
                    setMinistryFilter(e.target.value);
                    // Changing ministry invalidates the chosen department.
                    setValue('departmentId', '');
                  }}
                  label="Ministry"
                  initialOptions={ministryOptions}
                  placeholder="Search and select ministry"
                />
              </div>

              {/* Department - Searchable Dropdown (cascades from Ministry) */}
              <div className="md:col-span-2">
                <Controller
                  name="departmentId"
                  control={control}
                  render={({ field: { value, onChange, ...field } }) => (
                    <FormSearchableSelect
                      {...field}
                      value={value || ''}
                      onChange={(e) => onChange(e.target.value)}
                      label="Department"
                      onSearch={handleDepartmentSearch}
                      initialOptions={departmentOptions}
                      placeholder="Search and select department"
                      error={!!errors.departmentId}
                      helperText={errors.departmentId?.message}
                    />
                  )}
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 justify-end">
            <Button
              label="Cancel"
              severity="secondary"
              outlined
              onClick={() => handleNavigate('/unit-types')}
              disabled={isSaving}
            />
            <Button
              type="submit"
              label={isSaving ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
              icon={isSaving ? 'pi pi-spin pi-spinner' : undefined}
              disabled={isSaving}
            />
          </div>
        </form>
      </div>

      {/* Leave confirmation */}
      <DiscardChangesDialog
        visible={showLeaveDialog}
        onStay={cancelLeave}
        onLeave={confirmLeave}
        testId="UnitTypeForm.Dialog.Leave"
      />
    </>
  );
};

export default UnitTypeForm;
